import posthog from "posthog-js";
import { logger } from "@/infrastructure/logger";

let isInitialized = false;

/**
 * Initialize PostHog analytics.
 * Only initializes in non-development environments with a valid key.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";

  if (!key || environment === "development") {
    logger.info(
      `[Analytics] PostHog disabled (environment: ${environment}, key: ${key ? "present" : "missing"})`,
    );
    return;
  }

  try {
    posthog.init(key, {
      api_host: host || "https://eu.i.posthog.com",
      person_profiles: "identified_only",
      loaded: (ph) => {
        ph.register({ environment });
      },
    });
    isInitialized = true;
    logger.info(`[Analytics] PostHog initialized (environment: ${environment})`);
  } catch (error) {
    logger.error("[Analytics] Failed to initialize PostHog:", error);
  }
}

/**
 * Identify a user in PostHog after authentication.
 */
export function identifyUser(user: {
  id: string;
  uuid?: string;
  email: string;
  name: string;
  role: string;
}): void {
  if (!isInitialized) return;

  posthog.identify(user.uuid || user.id, {
    email: user.email,
    name: user.name,
    role: user.role,
  });
}

/**
 * Reset PostHog identity on logout.
 */
export function resetAnalytics(): void {
  if (!isInitialized) return;

  posthog.reset();
}

/**
 * Track a user-facing error or warning displayed via toast.
 */
export function trackUserFacingError(toast: {
  message: string;
  title?: string;
  type: string;
}): void {
  if (!isInitialized) return;

  posthog.capture("user_facing_error", {
    message: toast.message,
    title: toast.title,
    type: toast.type,
    page_path: window.location.pathname,
  });
}
